import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { useTranslation } from 'react-i18next'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { InputText } from 'primereact/inputtext'
import LayoutContainer from 'Components/LayoutComponent'
import { DialogModal } from 'Components/UIComponents'
import { ROUTE_STRINGS, TABLE_CONST } from 'Utils/Constants'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import { commonMethods } from 'Utils/commonMethods'
import Loader from 'Components/Loader'
import { Dropdown } from 'primereact/dropdown'
import AddForexData from './AddForexData'
import EditForexData from './EditForexData'

const ListForexData = ({ setLoading, mockForexData, mockForexDataList }) => {
  let rowCount = 8
  let paginationButtons = TABLE_CONST.paginationButtonCount
  const { t } = useTranslation()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const history = useHistory()
  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onConfirmation: () => {}, onSuccess: () => {} })
  const { utcToLocalShortDate } = commonMethods

  const baseCurrencyList = [
    {
      baseCountryId: 235,
      baseCountryIso3: 'USA',
      baseCountryName: 'United States',
      baseCurrencyCode: 'USD',
      baseCurrencyName: 'US Dollar',
      currencyLabel: 'USD - US Dollar'
    }
  ]

  const isEditModule = searchParams.get('edit-forex-data') // is Forex Edit screen
  const isAddModule = searchParams.get('add-forex-data') // is Forex Add screen
  const baseCurrencyFromParams = searchParams.get('baseCurrency')
  const targetCurrencyFromParams = searchParams.get('targetCurrency')
  const defaultBaseCurrency = 'USD'
  const defaultTargetCurrency = 'INR'

  const [forexDataList, setForexDataList] = useState([])
  const [targetCurrenyTypeList, setTargetCurrenyTypeList] = useState([])
  const [filteredForexDataList, setFilteredForexDataList] = useState([])
  const [activeModule, setActiveModule] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoadingForexData, setIsLoadingForexData] = useState(false)
  const [selectedBaseCurrnecy, setSelectedBaseCurrnecy] = useState(baseCurrencyList[0])
  const [selectedTargetCurrnecy, setSelectedTargetCurrnecy] = useState(null)

  useEffect(() => {
    init()

    // testing purpose
    if (mockForexData) {
      setForexDataList(mockForexDataList)
    }
  }, [])

  useEffect(() => {
    if (window.location.search.includes('edit-forex-data')) {
      setActiveModule('edit-forex-data')
    } else if (window.location.search.includes('add-forex-data')) {
      setActiveModule('add-forex-data')
    } else {
      setActiveModule('list-forex-data')
    }
  }, [window.location.search])

  if (window.innerWidth < TABLE_CONST.maxWidthTab) {
    rowCount = TABLE_CONST.mobileRowCount
    paginationButtons = TABLE_CONST.mobilePaginationButtonCount
  }

  const init = () => {
    getAllCurrencyTypeList()

    if (baseCurrencyFromParams || targetCurrencyFromParams || isAddModule || isEditModule) {
      return
    }

    history.replace({ pathname: ROUTE_STRINGS.listForexData, search: `?baseCurrency=${defaultBaseCurrency}&targetCurrency=${defaultTargetCurrency}` })
  }

  const getAllForexDayDataByTargetBaseCurrency = async (baseCurrencyCode, targetCurrencyCode) => {
    setLoading(true)
    setIsLoadingForexData(true)

    try {
      const response = await apiAdapterCoffeeWeb.getAllForexDayDataByTargetBaseCurrency(baseCurrencyCode, targetCurrencyCode)

      if (response?.data?.isSuccess) {
        setForexDataList(response?.data?.returnLst)
      } else {
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'error',
          message: t('FAILED_TO_GET_FOREX_DATA'),
          onHide: handleDismiss
        })
      }
    } catch {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('FAILED_TO_GET_FOREX_DATA'),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
      setIsLoadingForexData(false)
    }
  }

  const getAllCurrencyTypeList = async () => {
    setLoading(true)

    try {
      const response = await apiAdapterCoffeeWeb.getAllCurrencyTypeList()

      if (response?.data?.isSuccess) {
        const modifiedCurrencyList = response?.data?.returnLst.map((ele) => ({ ...ele, currencyLabel: `${ele.targetCurrency} - ${ele.targetCurrencyName}` }))
        const defaultCurrency = modifiedCurrencyList.find((ele) => ele.targetCurrencyCode === (targetCurrencyFromParams || defaultTargetCurrency)) || modifiedCurrencyList[0]

        getAllForexDayDataByTargetBaseCurrency(defaultBaseCurrency, defaultCurrency?.targetCurrencyCode)

        setSelectedTargetCurrnecy(defaultCurrency)
        setTargetCurrenyTypeList(modifiedCurrencyList)
      } else {
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'error',
          message: t('FAILED_TO_GET_CURRENCY_LIST'),
          onHide: handleDismiss
        })
      }
    } catch {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('FAILED_TO_GET_CURRENCY_LIST'),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSearchForexData = (e) => {
    const { value } = e.target
    const filteredForexDataList = forexDataList?.filter((ele) => {
      const searchTerm = value.toLowerCase()

      return utcToLocalShortDate(ele?.dataDtm)?.toLowerCase()?.includes(searchTerm)
    })

    setSearchQuery(value)
    setFilteredForexDataList(filteredForexDataList)
  }

  const handleClickAddButton = () => {
    history.push({ pathname: ROUTE_STRINGS.listForexData, search: 'add-forex-data=true' })
    setSearchQuery('')
  }

  const handleClickEditButton = (rowData) => {
    history.push({ pathname: ROUTE_STRINGS.listForexData, search: `edit-forex-data=true&forexId=${rowData.id}` })
  }

  const handleClickDeleteButton = (rowData) => {
    setShowDialog({
      ...showDialog,
      showModal: true,
      modalType: 'info',
      message: t('ARE_YPU_SURE_WANT_TO_DELETE_THIS_FOREX_DATA'),
      onConfirmation: () => deleteConfirmation(rowData),
      onHide: handleDismiss
    })
  }

  const handleBaseCurrencyDropdownChange = (e) => {
    const selectedBaseCurrencyCode = e.value.targetCurrencyCode

    history.replace({ pathname: ROUTE_STRINGS.listForexData, search: `?baseCurrency=${selectedBaseCurrencyCode}&targetCurrency=${selectedTargetCurrnecy.targetCurrencyCode}` })

    getAllForexDayDataByTargetBaseCurrency(selectedBaseCurrencyCode, selectedTargetCurrnecy.targetCurrencyCode)

    setSelectedBaseCurrnecy(e.value)
    setSearchQuery('')
  }

  const handleTargetCurrencyDropdownChange = (e) => {
    const selectedTargetCurrencyCode = e.value.targetCurrencyCode

    history.replace({ pathname: ROUTE_STRINGS.listForexData, search: `?baseCurrency=${selectedBaseCurrnecy.baseCurrencyCode}&targetCurrency=${selectedTargetCurrencyCode}` })

    getAllForexDayDataByTargetBaseCurrency(selectedBaseCurrnecy.baseCurrencyCode, selectedTargetCurrencyCode)

    setSelectedTargetCurrnecy(e.value)
    setSearchQuery('')
  }

  const deleteConfirmation = async (rowData) => {
    const { id } = rowData

    handleDismiss()
    setLoading(true)
    try {
      // API to delete forex data
      const res = await apiAdapterCoffeeWeb.deleteForexDayDataById(id)

      if (res?.data) {
        setForexDataList(forexDataList.filter((ele) => ele?.id !== id))
        setFilteredForexDataList(filteredForexDataList.filter((ele) => ele?.id !== id))
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'success',
          message: t('FOREX_DATA_HAS_BEEN_DELETED_SUCCESSFULLY'),
          onHide: handleSuccess,
          onSuccess: handleSuccess
        })
      }
    } catch (error) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: error.response.data.message,
        onHide: handleDismiss,
        onRetry: deleteConfirmation
      })
    } finally {
      setLoading(false)
    }
  }

  const renderDateColumn = (item) => {
    const formattedDate = utcToLocalShortDate(item.dataDtm)

    return <div>{formattedDate}</div>
  }

  const renderBaseCurrencyColumn = (item) => (
    <div>
      {item.baseCurrencyCode} - {item.baseCurrencyName}
    </div>
  )

  const renderTargetCurrencyColumn = (item) => (
    <div>
      {item.targetCurrencyCode} - {item.targetCurrencyName}
    </div>
  )

  const renderActionButtons = (rowData) => (
    <div className="action-buttons-wrapper">
      <Button type="button" icon="pi pi-pencil" className="edit-button" tooltip={t('EDIT')} tooltipOptions={{ position: 'top' }} onClick={() => handleClickEditButton(rowData)} data-testid="edit-button" />
      <Button type="button" icon="pi pi-trash" className="config-button" tooltip={t('DELETE')} tooltipOptions={{ position: 'top' }} onClick={() => handleClickDeleteButton(rowData)} data-testid="config-button" />
    </div>
  )

  const tableResponseMessage = () => <div>{isLoadingForexData ? t('FETCHING') : t('NO_RESULTS_FOUND')}</div>

  const RenderForexDataTable = () => (
    <DataTable emptyMessage={tableResponseMessage} value={searchQuery ? filteredForexDataList : forexDataList} paginator={searchQuery ? filteredForexDataList?.length : forexDataList?.length} rows={rowCount} pageLinkSize={paginationButtons} tableStyle={{ minWidth: '50rem' }}>
      <Column field="dataDtm" body={(e) => renderDateColumn(e)} header={t('DATE')}></Column>
      <Column field="baseCurrencyCode" body={(e) => renderBaseCurrencyColumn(e)} header={t('BASE_CURRENCY')}></Column>
      <Column field="targetCurrencyCode" body={(e) => renderTargetCurrencyColumn(e)} header={t('TARGET_CURRENCY')}></Column>
      <Column field="currencyOpen" header={t('OPEN')}></Column>
      <Column field="currencyHigh" header={t('HIGH')}></Column>
      <Column field="currencyLow" header={t('LOW')}></Column>
      <Column field="currencyClose" header={t('Close')}></Column>
      <Column field="currencyPreClose" header={t('PRE_CLOSE')}></Column>
      <Column field="currencyNetChg" header={t('NET_CHANGE')}></Column>
      <Column field="currencyPctChg" header={t('PERCENTAGE_CHANGE')}></Column>
      <Column className="action-buttons-column-cell" body={(e) => renderActionButtons(e)} field="quantity" header={t('ACTIONS')}></Column>
    </DataTable>
  )

  const handleSuccess = () => setShowDialog({ ...showDialog, showModal: false })
  const handleDismiss = () => setShowDialog({ ...showDialog, showModal: false })

  const getPageTitle = () => {
    /* eslint-disable no-else-return */
    if (activeModule === 'list-forex-data') {
      return t('LIST_FOREX_DATA')
    } else if (activeModule === 'add-forex-data') {
      return t('ADD_FOREX_DATA')
    } else {
      return t('EDIT_FOREX_DATA')
    }
  }

  return (
    <LayoutContainer title={getPageTitle()} breadCrumbsTitle={'listForex'} showAddButton={activeModule === 'list-forex-data'} handleAddButtonClick={handleClickAddButton}>
      <div className="forex-component-wrapper">
        {activeModule === 'list-forex-data' && (
          <div className="forex-list-tab-wrapper">
            <div className="forex-list-header">
              <div className="dropdown-wrapper">
                <label htmlFor="baseCurrency">{t('BASE_CURRENCY')}</label>
                <Dropdown value={selectedBaseCurrnecy} onChange={(e) => handleBaseCurrencyDropdownChange(e)} options={baseCurrencyList} optionLabel="currencyLabel" className="w-full md:w-14rem" data-testid="base-currency-dropdown" />
              </div>
              <div className="dropdown-wrapper">
                <label htmlFor="targetCurrency">{t('TARGET_CURRENCY')}</label>
                <Dropdown value={selectedTargetCurrnecy} onChange={(e) => handleTargetCurrencyDropdownChange(e)} options={targetCurrenyTypeList} optionLabel="currencyLabel" className="w-full md:w-14rem" data-testid="target-currency-dropdown" />
              </div>
              <div className="dropdown-wrapper">
                <label htmlFor="date">{t('DATE')}</label>
                <InputText placeholder={t('SEARCH_BY_DATE')} value={searchQuery} onChange={(e) => handleSearchForexData(e)} data-testid="date-search-field" />
              </div>
            </div>
            <RenderForexDataTable />
          </div>
        )}
        {activeModule === 'add-forex-data' && <AddForexData forexDataList={forexDataList} setForexDataList={setForexDataList} baseCurrencyList={baseCurrencyList} targetCurrenyTypeList={targetCurrenyTypeList} selectedTargetCurrnecyFromParent={selectedTargetCurrnecy} />}
        {activeModule === 'edit-forex-data' && <EditForexData forexDataList={forexDataList} setForexDataList={setForexDataList} baseCurrencyList={baseCurrencyList} targetCurrenyTypeList={targetCurrenyTypeList} filteredForexDataList={filteredForexDataList} setFilteredForexDataList={setFilteredForexDataList} />}

        <DialogModal {...showDialog} onDismiss={handleDismiss} />
      </div>
    </LayoutContainer>
  )
}

export default Loader(ListForexData)
